import { useState } from "react";
import Cropper from "react-easy-crop";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { ImagePlus, ZoomIn, ZoomOut, Move, Loader2 } from "lucide-react";

const BOT_AVATAR = "https://static.prod-images.emergentagent.com/jobs/0c40222b-65cc-42fc-9117-09f0a7b6df59/images/beda8625d87f4cb78a04f33d14ef71520eb3fdaa5585e39b6568a20b616c3ff7.png";

export function BotAvatarUpload({ bot, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState(null);

  const avatar = bot?.avatar || BOT_AVATAR;
  const avatarSrc = avatar?.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${avatar}` : avatar;

  const resetCrop = () => { setCrop({ x: 0, y: 0 }); setZoom(1); setArea(null); };

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("Image too large. Maximum size is 5 MB."); return; }
    setPreview((prev) => { if (prev?.startsWith("blob:")) URL.revokeObjectURL(prev); return URL.createObjectURL(f); });
    setFile(f);
    resetCrop();
  };

  const close = (o) => {
    setOpen(o);
    if (!o) {
      setPreview((prev) => { if (prev?.startsWith("blob:")) URL.revokeObjectURL(prev); return null; });
      setFile(null);
      resetCrop();
    }
  };

  const getCroppedBlob = (src, a) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => {
        const size = 512;
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(image, a.x, a.y, a.width, a.height, 0, 0, size, size);
        canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("Crop failed"))), "image/jpeg", 0.9);
      };
      image.onerror = () => reject(new Error("Could not load image"));
      image.src = src;
    });

  const upload = async () => {
    if (!file || !bot || !area) return;
    setUploading(true);
    try {
      const blob = await getCroppedBlob(preview, area);
      const form = new FormData();
      form.append("file", new File([blob], "avatar.jpg", { type: "image/jpeg" }));
      await api.post(`/bots/${bot.id}/avatar`, form);
      toast.success("Profile picture updated");
      close(false);
      if (onUpdated) await onUpdated();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-4">
        <div className="h-16 w-16 rounded-full overflow-hidden border-2 shrink-0 flex items-center justify-center"
          style={{ borderColor: "var(--sfx-accent)", background: "radial-gradient(circle at 50% 40%, #0b1a2e, #04060d)" }}>
          <img src={avatarSrc} alt={bot?.name} data-testid="settings-bot-avatar" className="h-full w-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-semibold text-sm text-white truncate">{bot?.name || "Robot"}</div>
          <p className="text-xs text-[#5f7ba6]">Upload, crop and zoom a new picture for your robot.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="change-bot-picture-button"
          className="h-9 rounded-full px-4 text-white font-semibold hover:opacity-90 shrink-0" style={{ backgroundColor: "var(--sfx-accent)" }}>
          <ImagePlus className="mr-1.5 h-4 w-4" /> Change
        </Button>
      </div>

      <Dialog open={open} onOpenChange={close}>
        <DialogContent className="bg-[#0A1120] border-[#14233d] text-white max-w-[420px]" data-testid="avatar-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Update {bot?.name} picture</DialogTitle>
            <DialogDescription className="text-[#5f7ba6]">Upload an image, then crop, zoom and drag to frame it.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-1">
            {preview ? (
              <>
                <div className="relative w-full h-64 bg-black rounded-2xl overflow-hidden" data-testid="avatar-cropper">
                  <Cropper
                    image={preview}
                    crop={crop}
                    zoom={zoom}
                    minZoom={1}
                    maxZoom={4}
                    aspect={1}
                    cropShape="round"
                    showGrid={false}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={(_, areaPixels) => setArea(areaPixels)}
                  />
                </div>
                <div className="flex items-center gap-2 text-[#5f7ba6] text-xs">
                  <Move className="h-3.5 w-3.5" /> Drag to reposition · use the slider to zoom
                </div>
                <div className="flex items-center gap-3 w-full px-1">
                  <ZoomOut className="h-4 w-4 text-[#5f7ba6] shrink-0" />
                  <Slider data-testid="avatar-zoom-slider" value={[zoom]} min={1} max={4} step={0.05}
                    onValueChange={([z]) => setZoom(z)} className="flex-1" />
                  <ZoomIn className="h-4 w-4 text-[#5f7ba6] shrink-0" />
                </div>
                <label htmlFor="settings-avatar-file" data-testid="avatar-choose-button"
                  className="cursor-pointer text-xs font-semibold text-[#8fb4e6] hover:text-white transition-colors">
                  Choose a different image
                </label>
              </>
            ) : (
              <>
                <div className="h-32 w-32 rounded-full overflow-hidden border-2 flex items-center justify-center"
                  style={{ borderColor: "var(--sfx-accent)", background: "radial-gradient(circle at 50% 40%, #0b1a2e, #04060d)" }}>
                  <img src={avatarSrc} alt="current" data-testid="avatar-preview" className="h-full w-full object-cover" />
                </div>
                <label htmlFor="settings-avatar-file" data-testid="avatar-choose-button"
                  className="cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-[#24406b] bg-transparent text-white text-sm font-semibold hover:bg-[#0f1a2e] transition-colors">
                  <ImagePlus className="h-4 w-4" /> Choose Image
                </label>
              </>
            )}
            <input id="settings-avatar-file" data-testid="avatar-file-input" type="file" accept="image/*" onChange={pick} className="hidden" />
            <p className="text-xs text-[#5f7ba6] text-center">JPG, PNG, WebP or GIF · max 5 MB</p>
            <Button onClick={upload} disabled={!file || !area || uploading} data-testid="avatar-save-button"
              className="w-full h-11 rounded-full text-white font-semibold hover:opacity-90" style={{ backgroundColor: "var(--sfx-accent)" }}>
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crop & Save Picture"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
